import {
  CreditRating,
  DefaultFinanceTerms,
  PurchaseTermNames,
  LeaseMileageNames,
  MileageType,
  DEFAULT_APR,
  PRICE_OVERRIDE_FORWARDED_DEFAULT_EXPIRY,
  TaxType,
  LeaseTermNames,
  Origin,
} from '@upstart/harmony/lib/constants';
import { get } from 'lodash';
import { isMarketScanPaymentsEnabled } from '@upstart/harmony/lib/dealer-utils';
import { getDefaultTaxTypeLabel } from '../../util/pencilUtils';

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;
const lastYear = currentYear - 1;
const yearBeforeLast = lastYear - 1;

export const step1Schema = {
  title: 'Online Pricing Matrix',
  type: 'object',
  required: [],
  properties: {
    onlinePricingMatrix: {
      type: 'boolean',
    },
  },
};

export const step2Schema = {
  title: 'In-Store Pricing Matrix',
  type: 'object',
  required: [],
  properties: {
    instorePricingMatrix: {
      type: 'boolean',
    },
  },
};

const getTaxTypeEntryProps = ({
  title,
  showUseTax,
  isMarketScanPaymentsEnabledFlag,
  requiredFields,
}) => ({
  type: 'object',
  title,
  required: requiredFields,
  properties: {
    name: {
      type: 'string',
      title: 'Tax Name',
      default: title,
      required: [],
    },
    rate: {
      type: 'number',
      title: 'Tax Rate',
      required: [],
    },
    ...(showUseTax
      ? {
          useRate: {
            type: 'number',
            description:
              'Only applies to Lease for some states, defaults to Tax Rate',
            title: 'Use Rate',
            required: [],
          },
        }
      : {}),
    ...(!isMarketScanPaymentsEnabledFlag
      ? {
          maxAmount: {
            type: 'number',
            description: 'Cap on the maximum amount of this tax',
            title: 'Maximum Tax Amount',
            required: [],
          },
        }
      : {}),
  },
});

const getTaxProps = ({
  title,
  disableTaxes,
  showUseTax,
  isMarketScanPaymentsEnabledFlag,
}) => {
  const requiredFields = disableTaxes ? [] : ['name', 'rate'];

  return {
    type: 'object',
    title,
    properties: {
      [TaxType.STATE]: getTaxTypeEntryProps({
        title: getDefaultTaxTypeLabel(TaxType.STATE),
        showUseTax,
        isMarketScanPaymentsEnabledFlag,
        requiredFields,
      }),
      [TaxType.COUNTY]: getTaxTypeEntryProps({
        title: getDefaultTaxTypeLabel(TaxType.COUNTY),
        showUseTax,
        isMarketScanPaymentsEnabledFlag,
      }),
      [TaxType.CITY]: getTaxTypeEntryProps({
        title: getDefaultTaxTypeLabel(TaxType.CITY),
        showUseTax,
        isMarketScanPaymentsEnabledFlag,
      }),
    },
  };
};

const getStep3Taxes = (disableTaxes, isMarketScanPaymentsEnabledFlag) => ({
  type: 'object',
  title: 'Taxes',
  properties: {
    cash: getTaxProps({
      title: 'Cash',
      disableTaxes,
      showUseTax: false,
      isMarketScanPaymentsEnabledFlag,
    }),
    purchase: getTaxProps({
      title: 'Purchase',
      disableTaxes,
      showUseTax: false,
      isMarketScanPaymentsEnabledFlag,
    }),
    lease: getTaxProps({
      title: 'Lease',
      disableTaxes,
      showUseTax: true,
      isMarketScanPaymentsEnabledFlag,
    }),
  },
});

const step3Addons = ({ useCPO }) => ({
  type: 'object',
  title: 'Add Ons',
  properties: {
    New: {
      type: 'array',
      title: 'New Cars',
      $ref: '#/definitions/addOnRef',
    },
    ...(useCPO
      ? {
          CPO: {
            type: 'array',
            title: 'CPO Cars',
            $ref: '#/definitions/addOnRef',
          },
        }
      : {}),
    Used: {
      type: 'array',
      title: 'Used Cars',
      $ref: '#/definitions/addOnRef',
    },
  },
});

const step3Schema = ({
  disableTaxes,
  useCPO,
  isMarketScanPaymentsEnabledFlag,
}) => {
  const codeSchema = {
    code: { type: 'string', title: 'Code', minLength: 0, maxLength: 50 },
  };

  return {
    definitions: {
      addOnRef: {
        type: 'array',
        title: 'New Cars',
        minItems: 0,
        maxItems: 50,
        items: {
          type: 'object',
          required: ['name'],
          properties: {
            name: {
              type: 'string',
              title: 'Name',
              maxLength: 50,
            },
            price: {
              type: 'number',
              title: 'Price',
              minimum: 0,
              maximum: 100000,
            },
            cost: {
              type: 'number',
              title: 'Cost',
              minimum: 0,
              maximum: 100000,
            },
            taxableForLease: {
              type: 'boolean',
              title: 'Taxable For Lease',
              enum: [true, false],
              default: false,
            },
            ...codeSchema,
          },
        },
      },
    },
    title: 'Taxes & Add-Ons',
    type: 'object',
    required: [],
    properties: {
      featureConfig: {
        type: 'object',
        title: '',
        properties: {
          disableTaxes: {
            type: 'boolean',
            title: 'Disable Taxes',
          },
          taxByDealerAddress: {
            title:
              'Use dealer address for tax calculation for all Finance Types (This field is unusable and un-clickable if MS Taxes flags are disabled)',
            type: 'boolean',
            enum: [true, false],
            default: false,
          },
          useWarrantyTaxForPurchaseAndCash: {
            title: 'Use Warranty Tax for Purchase and Cash',
            type: 'boolean',
          },
          useWarrantyTaxForLease: {
            title: 'Use Warranty Tax for Lease',
            type: 'boolean',
          },
        },
      },
      desking: {
        type: 'object',
        title: '',
        properties: {
          useCapitalizedTax: {
            type: 'boolean',
            title: 'Use Capitalized Tax Rule - % of downpayment (Lease)',
            default: false,
          },
        },
        dependencies: {
          useCapitalizedTax: {
            oneOf: [
              {
                properties: {
                  useCapitalizedTax: {
                    enum: [false],
                  },
                },
              },
              {
                properties: {
                  useCapitalizedTax: {
                    enum: [true],
                  },
                  capitalizedTaxRate: {
                    type: 'number',
                    title: 'Capitalized tax rate',
                    default: 0.07,
                    minimum: 0,
                    maximum: 1,
                  },
                },
                required: ['capitalizedTaxRate'],
              },
            ],
          },
        },
      },
      taxes: getStep3Taxes(disableTaxes, isMarketScanPaymentsEnabledFlag),
      addOns: step3Addons({ useCPO }),
    },
  };
};

export const getStep3Schema = (data) =>
  step3Schema({
    disableTaxes: get(data, 'featureConfig.disableTaxes', false),
    useCPO: get(data, 'featureConfig.useCPO.enabled', false),
    isMarketScanPaymentsEnabledFlag: isMarketScanPaymentsEnabled(data),
  });

const aprConfigDefinitions = {
  purchaseApr: {
    type: 'object',
    title: '',
    required: [
      PurchaseTermNames.TERM1,
      PurchaseTermNames.TERM2,
      PurchaseTermNames.TERM3,
    ],
    properties: {
      [PurchaseTermNames.TERM1]: {
        type: 'number',
        title: 'APR % - Term 1',
        minimum: 0,
        maximum: 100,
        default: DEFAULT_APR,
      },
      [PurchaseTermNames.TERM2]: {
        type: 'number',
        title: 'APR % - Term 2',
        minimum: 0,
        maximum: 100,
        default: DEFAULT_APR,
      },
      [PurchaseTermNames.TERM3]: {
        type: 'number',
        title: 'APR % - Term 3',
        minimum: 0,
        maximum: 100,
        default: DEFAULT_APR,
      },
    },
  },
  aprMarkup: {
    type: 'object',
    title: '',
    properties: {
      [PurchaseTermNames.TERM1]: {
        type: ['number', 'null'],
        title: 'Special OEM Markup %',
        minimum: 0,
        maximum: 5,
      },
      [PurchaseTermNames.TERM2]: {
        type: ['number', 'null'],
        title: 'Special OEM Markup %',
        minimum: 0,
        maximum: 5,
      },
      [PurchaseTermNames.TERM3]: {
        type: ['number', 'null'],
        title: 'Special OEM Markup %',
        minimum: 0,
        maximum: 5,
      },
    },
  },
};

const getLeaseMoneyFactorSchema = (forOnePayLease) => ({
  type: 'object',
  title: '',
  properties: {
    [LeaseMileageNames.MILEAGE1]: {
      type: ['number', 'null'],
      title: `Mileage 1 - ${
        forOnePayLease ? 'One Pay' : ''
      } Money Factor Mark Up (Basis Points)`,
      minimum: 0,
      maximum: 1000,
    },
    [LeaseMileageNames.MILEAGE2]: {
      type: ['number', 'null'],
      title: `Mileage 2 - ${
        forOnePayLease ? 'One Pay' : ''
      } Money Factor Mark Up (Basis Points)`,
      minimum: 0,
      maximum: 1000,
    },
    [LeaseMileageNames.MILEAGE3]: {
      type: ['number', 'null'],
      title: `Mileage 3 - ${
        forOnePayLease ? 'One Pay' : ''
      } Money Factor Mark Up (Basis Points)`,
      minimum: 0,
      maximum: 1000,
    },
    [LeaseMileageNames.MILEAGE4]: {
      type: ['number', 'null'],
      title: `Mileage 4 - ${
        forOnePayLease ? 'One Pay' : ''
      } Money Factor Mark Up (Basis Points)`,
      minimum: 0,
      maximum: 1000,
    },
    [LeaseMileageNames.MILEAGE5]: {
      type: ['number', 'null'],
      title: `Mileage 5 - ${
        forOnePayLease ? 'One Pay' : ''
      } Money Factor Mark Up (Basis Points)`,
      minimum: 0,
      maximum: 1000,
    },
  },
});

const getLeaseTermSchema = ({
  termTitle,
  defaultValue,
  required = [],
  onePayLeaseEnabled,
}) => {
  const termRequired = required.includes('term');
  const properties = {
    moneyFactor: getLeaseMoneyFactorSchema(false),
  };
  if (onePayLeaseEnabled) {
    properties.onePayLeaseMoneyFactor =
      getLeaseMoneyFactorSchema(onePayLeaseEnabled);
  }
  return {
    type: 'object',
    title: '',
    required,
    properties: {
      term: {
        type: ['integer', ...(termRequired ? [] : ['null'])],
        title: `${termTitle} ${termRequired ? '' : '(Optional)'}`,
        minimum: 1,
        maximum: 99,
        default: defaultValue,
      },
      credit: {
        type: 'object',
        title: '',
        properties: {
          [CreditRating.POOR]: {
            title: 'Below Average',
            type: 'object',
            properties,
          },
          [CreditRating.AVERAGE]: {
            title: 'Average',
            type: 'object',
            properties,
          },
          [CreditRating.GOOD]: {
            title: 'Good',
            type: 'object',
            properties,
          },
          [CreditRating.EXCELLENT]: {
            title: 'Excellent',
            type: 'object',
            properties,
          },
        },
      },
    },
  };
};

export const aprConfigSchema = (onePayLeaseEnabled) => ({
  definitions: aprConfigDefinitions,
  type: 'object',
  title: '',
  properties: {
    enabled: {
      title: 'Enabled',
      type: 'boolean',
      enum: [true, false],
      default: false,
    },
  },
  dependencies: {
    enabled: {
      oneOf: [
        {
          properties: {
            enabled: {
              enum: [false],
            },
          },
        },
        {
          properties: {
            enabled: {
              enum: [true],
            },
            creditTiers: {
              type: 'object',
              title: 'Credit Tiers',
              properties: {
                [CreditRating.POOR]: {
                  title: 'Tier - Below Average',
                  type: 'object',
                  properties: {
                    low: {
                      type: 'integer',
                      title: 'Low Range',
                      default: 500,
                      minimum: 0,
                      maximum: 850,
                    },
                    high: {
                      type: 'integer',
                      title: 'High Range',
                      default: 600,
                      minimum: 0,
                      maximum: 850,
                    },
                  },
                },
                [CreditRating.AVERAGE]: {
                  title: 'Tier - Average',
                  type: 'object',
                  properties: {
                    low: {
                      type: 'integer',
                      title: 'Low Range',
                      default: 601,
                      minimum: 0,
                      maximum: 850,
                    },
                    high: {
                      type: 'integer',
                      title: 'High Range',
                      default: 650,
                      minimum: 0,
                      maximum: 850,
                    },
                  },
                },
                [CreditRating.GOOD]: {
                  title: 'Tier - Good',
                  type: 'object',
                  properties: {
                    low: {
                      type: 'integer',
                      title: 'Low Range',
                      default: 651,
                      minimum: 0,
                      maximum: 850,
                    },
                    high: {
                      type: 'integer',
                      title: 'High Range',
                      default: 750,
                      minimum: 0,
                      maximum: 850,
                    },
                  },
                },
                [CreditRating.EXCELLENT]: {
                  title: 'Tier - Excellent',
                  type: 'object',
                  properties: {
                    low: {
                      type: 'integer',
                      title: 'Low Range',
                      default: 751,
                      minimum: 0,
                      maximum: 850,
                    },
                    high: {
                      type: 'integer',
                      title: 'High Range',
                      default: 850,
                      minimum: 0,
                      maximum: 850,
                    },
                  },
                },
              },
            },
            purchase: {
              type: 'object',
              title: 'Finance - Terms',
              properties: {
                terms: {
                  type: 'object',
                  title: '',
                  properties: {
                    [PurchaseTermNames.TERM1]: {
                      type: 'integer',
                      title: 'Finance Term 1',
                      default: DefaultFinanceTerms.PURCHASE[0],
                      minimum: 0,
                      maximum: 100,
                    },
                    [PurchaseTermNames.TERM2]: {
                      type: 'integer',
                      title: 'Finance Term 2',
                      default: DefaultFinanceTerms.PURCHASE[1],
                      minimum: 0,
                      maximum: 100,
                    },
                    [PurchaseTermNames.TERM3]: {
                      type: 'integer',
                      title: 'Finance Term 3',
                      default: DefaultFinanceTerms.PURCHASE[2],
                      minimum: 0,
                      maximum: 100,
                    },
                  },
                },
                credit: {
                  type: 'object',
                  title: '',
                  properties: {
                    [CreditRating.POOR]: {
                      title: 'Finance - Below Average',
                      type: 'object',
                      properties: {
                        apr: {
                          $ref: '#/definitions/purchaseApr',
                        },
                        markup: {
                          $ref: '#/definitions/aprMarkup',
                        },
                      },
                    },
                    [CreditRating.AVERAGE]: {
                      title: 'Finance - Average',
                      type: 'object',
                      properties: {
                        apr: {
                          $ref: '#/definitions/purchaseApr',
                        },
                        markup: {
                          $ref: '#/definitions/aprMarkup',
                        },
                      },
                    },
                    [CreditRating.GOOD]: {
                      title: 'Finance - Good',
                      type: 'object',
                      properties: {
                        apr: {
                          $ref: '#/definitions/purchaseApr',
                        },
                        markup: {
                          $ref: '#/definitions/aprMarkup',
                        },
                      },
                    },
                    [CreditRating.EXCELLENT]: {
                      title: 'Finance - Excellent',
                      type: 'object',
                      properties: {
                        apr: {
                          $ref: '#/definitions/purchaseApr',
                        },
                        markup: {
                          $ref: '#/definitions/aprMarkup',
                        },
                      },
                    },
                  },
                },
              },
            },
            lease: {
              type: 'object',
              title: '',
              properties: {
                mileages: {
                  type: 'object',
                  title: 'Lease - Mileages',
                  required: [
                    LeaseMileageNames.MILEAGE1,
                    LeaseMileageNames.MILEAGE2,
                    LeaseMileageNames.MILEAGE3,
                  ],
                  properties: {
                    [LeaseMileageNames.MILEAGE1]: {
                      type: ['integer', 'null'],
                      title: 'Lease Mileage 1',
                      default: MileageType.LOW_MILEAGE,
                      minimum: 0,
                      maximum: 100000,
                    },
                    [LeaseMileageNames.MILEAGE2]: {
                      type: ['integer', 'null'],
                      title: 'Lease Mileage 2',
                      default: MileageType.STANDARD_MILEAGE,
                      minimum: 0,
                      maximum: 100000,
                    },
                    [LeaseMileageNames.MILEAGE3]: {
                      type: ['integer', 'null'],
                      title: 'Lease Mileage 3',
                      default: MileageType.HIGH_MILEAGE,
                      minimum: 0,
                      maximum: 100000,
                    },
                    [LeaseMileageNames.MILEAGE4]: {
                      type: ['integer', 'null'],
                      title: 'Lease Mileage 4 (Optional)',
                      minimum: 0,
                      maximum: 100000,
                    },
                    [LeaseMileageNames.MILEAGE5]: {
                      type: ['integer', 'null'],
                      title: 'Lease Mileage 5 (Optional)',
                      minimum: 0,
                      maximum: 100000,
                    },
                  },
                },
                terms: {
                  type: 'object',
                  title: ' ',
                  properties: {
                    [LeaseTermNames.TERM1]: getLeaseTermSchema({
                      termTitle: 'Term 1',
                      defaultValue: 36,
                      required: ['term'],
                      onePayLeaseEnabled,
                    }),
                    [LeaseTermNames.TERM2]: getLeaseTermSchema({
                      termTitle: 'Term 2',
                      onePayLeaseEnabled,
                    }),
                    [LeaseTermNames.TERM3]: getLeaseTermSchema({
                      termTitle: 'Term 3',
                      onePayLeaseEnabled,
                    }),
                    [LeaseTermNames.TERM4]: getLeaseTermSchema({
                      termTitle: 'Term 4',
                      onePayLeaseEnabled,
                    }),
                    [LeaseTermNames.TERM5]: getLeaseTermSchema({
                      termTitle: 'Term 5',
                      onePayLeaseEnabled,
                    }),
                  },
                },
              },
            },
            options: {
              type: 'object',
              title: 'Settings',
              properties: {
                addOEMMarkupRate: {
                  type: 'boolean',
                  title: 'Add OEM markup rate',
                  enum: [true, false],
                  default: false,
                },
                useManufacturerApr: {
                  type: 'object',
                  title: "Use Manufacturer's APR?",
                  properties: {
                    purchase: {
                      type: 'boolean',
                      title: 'Purchase',
                      enum: [true, false],
                      default: false,
                    },
                  },
                  dependencies: {
                    purchase: {
                      oneOf: [
                        {
                          properties: {
                            purchase: {
                              enum: [false],
                            },
                          },
                        },
                        {
                          properties: {
                            purchase: {
                              enum: [true],
                            },
                            [CreditRating.EXCELLENT]: {
                              title: 'Override Excellent APR?',
                              type: 'boolean',
                              enum: [true, false],
                              default: true,
                            },
                            [CreditRating.GOOD]: {
                              title: 'Override Good APR?',
                              type: 'boolean',
                              enum: [true, false],
                              default: true,
                            },
                            [CreditRating.AVERAGE]: {
                              title: 'Override Average APR?',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                            [CreditRating.POOR]: {
                              title: 'Override Poor APR?',
                              type: 'boolean',
                              enum: [true, false],
                              default: false,
                            },
                          },
                        },
                      ],
                    },
                  },
                },
                useManufacturerRebate: {
                  type: 'object',
                  title: "Use Manufacturer's Rebates?",
                  properties: {
                    purchase: {
                      type: 'boolean',
                      title: 'Purchase',
                      enum: [true, false],
                      default: false,
                    },
                    lease: {
                      type: 'boolean',
                      title: 'Lease',
                      enum: [true, false],
                      default: false,
                    },
                    cash: {
                      type: 'boolean',
                      title: 'Cash',
                      enum: [true, false],
                      default: false,
                    },
                  },
                },
              },
            },
          },
        },
      ],
    },
  },
});

export const step4Schema = (enableOnePayLease) => ({
  definitions: aprConfigDefinitions,
  title: 'Online APR Configuration',
  type: 'object',
  required: [],
  properties: {
    aprConfig: {
      title: '',
      type: 'object',
      properties: {
        New: aprConfigSchema(enableOnePayLease),
        Used: aprConfigSchema(enableOnePayLease),
      },
    },
  },
});

export const getStep4Schema = (data) =>
  step4Schema(get(data, 'desking.enableOnePayLease', false));

export const step5Schema = (enableOnePayLease) => ({
  definitions: aprConfigDefinitions,
  title: 'In-Store APR Configuration',
  type: 'object',
  required: [],
  properties: {
    inStoreAprConfig: {
      title: '',
      type: 'object',
      properties: {
        New: aprConfigSchema(enableOnePayLease),
        Used: aprConfigSchema(enableOnePayLease),
      },
    },
  },
});

export const getStep5Schema = (data) =>
  step5Schema(get(data, 'desking.enableOnePayLease', false));

export const step7Schema = {
  title: 'Global Pricing',
  type: 'object',
  properties: {
    featureConfig: {
      type: 'object',
      properties: {
        globalPricing: {
          type: 'object',
          required: ['forwardedPriceOverrideExpiration'],
          properties: {
            usePriceOverrides: {
              type: 'object',
              properties: {
                [Origin.INSTORE]: {
                  title: 'In-Store Price Overrides Enabled',
                  type: 'boolean',
                  enum: [true, false],
                  default: false,
                },
                [Origin.ONLINE]: {
                  title: 'Online Price Overrides Enabled',
                  type: 'boolean',
                  enum: [true, false],
                  default: false,
                },
                [Origin.FORWARDED]: {
                  title: 'Forwarded Price Overrides Enabled',
                  description: 'For prices from third party leads',
                  type: 'boolean',
                  enum: [true, false],
                  default: false,
                },
              },
            },
            forwardedPriceOverrideExpiration: {
              title: 'Forwarded Price Override Expiration',
              description:
                'How many days a price from a third party source is valid for',
              type: 'number',
              default: PRICE_OVERRIDE_FORWARDED_DEFAULT_EXPIRY,
            },
          },
        },
      },
    },
  },
};

export const step8Schema = {
  title: 'Display',
  type: 'object',
  properties: {
    displayConfig: {
      type: 'object',
      properties: {
        hideUsedCarListPrice: {
          title: 'Hide Used Car List Price',
          type: 'boolean',
          enum: [true, false],
          default: false,
        },
        showInstoreMSRP: {
          title: 'Show MSRP For Instore',
          type: 'boolean',
          enum: [true, false],
          default: true,
        },
        differentPriceSource: {
          title: 'Leads without PII',
          type: 'object',
          properties: {
            useDifferentPriceSourceForUnregistered: {
              title: 'Use MSRP for leads without PII',
              type: 'boolean',
              enum: [true, false],
              default: false,
            },
            availableCarsYears: {
              title:
                'Choose the car model years that will be considered for this option',
              type: 'object',
              properties: {
                nextYear: {
                  title: `${nextYear}`,
                  type: 'boolean',
                  enum: [true, false],
                  default: true,
                },
                currentYear: {
                  title: `${currentYear}`,
                  type: 'boolean',
                  enum: [true, false],
                  default: true,
                },
                lastYear: {
                  title: `${lastYear}`,
                  type: 'boolean',
                  enum: [true, false],
                  default: true,
                },
                yearBeforeLast: {
                  title: `${yearBeforeLast}`,
                  type: 'boolean',
                  enum: [true, false],
                  default: false,
                },
              },
            },
          },
        },
        showOnlineMSRP: {
          title: 'Show MSRP For Online',
          type: 'boolean',
          enum: [true, false],
          default: true,
        },
      },
    },
  },
};

export const step9Schema = {
  title: 'Sign&Drive',
  type: 'object',
  required: [],
  properties: {
    waiveFirstLeasePaymentYearMakeModels: {
      type: 'array',
      title: 'Sign&Drive',
    },
  },
};

export const getStep10Schema = () => ({
  type: 'object',
  title: 'Market Scan',
  properties: {
    marketScanConfig: {
      type: 'object',
      title: '',
      properties: {
        incentivesEnabled: {
          title: 'Use Market Scan Incentives',
          type: 'boolean',
          enum: [true, false],
          default: false,
        },
        paymentsEnabled: {
          title: 'Use Market Scan for Payment Calculations',
          type: 'boolean',
          enum: [true, false],
          default: false,
        },
        taxesEnabled: {
          type: 'boolean',
          title: 'Use Market Scan Standalone Taxes',
          enum: [true, false],
          default: false,
        },
        feesEnabled: {
          type: 'boolean',
          title: 'Use Market Scan Standalone Fees',
          enum: [true, false],
          default: false,
        },
      },
      dependencies: {
        paymentsEnabled: {
          oneOf: [
            {
              properties: {
                paymentsEnabled: {
                  enum: [false],
                },
              },
            },
            {
              required: ['accountNumber'],
              properties: {
                paymentsEnabled: {
                  enum: [true],
                },
                ...accountNumberSchema,
                ...marketScanTaxConfigurationSchema,
              },
            },
          ],
        },
        taxesEnabled: {
          oneOf: [
            {
              properties: {
                taxesEnabled: {
                  enum: [false],
                },
              },
            },
            {
              required: ['accountNumber'],
              properties: {
                taxesEnabled: {
                  enum: [true],
                },
                ...accountNumberSchema,
              },
            },
          ],
        },
        feesEnabled: {
          oneOf: [
            {
              properties: {
                feesEnabled: {
                  enum: [false],
                },
              },
            },
            {
              required: ['accountNumber'],
              properties: {
                feesEnabled: {
                  enum: [true],
                },
                ...accountNumberSchema,
                ...marketScanFeeConfigurationSchema,
              },
            },
          ],
        },
        incentivesEnabled: {
          oneOf: [
            {
              properties: {
                incentivesEnabled: {
                  enum: [false],
                },
              },
            },
            {
              required: ['accountNumber'],
              properties: {
                incentivesEnabled: {
                  enum: [true],
                },
                useLendersFromMSPortal: {
                  title: 'Market Scan Lenders',
                  type: 'boolean',
                  enum: [true, false],
                  enumNames: [
                    'Use lender list from Marketscan Partner Portal',
                    'Use captive only',
                  ],
                  default: false,
                },
                ...accountNumberSchema,
              },
            },
          ],
        },
      },
    },
  },
});

const accountNumberSchema = {
  accountNumber: {
    type: 'string',
    title: 'Account Number',
  },
};

const marketScanColoradoTaxConfig = {
  CO: {
    // Colorado
    type: 'object',
    title: 'Colorado',
    properties: {
      collectCityTaxInPayment: {
        type: 'boolean',
        title: 'Collect City Tax in Payment',
        enum: [true, false],
        default: false,
      },
      collectCountyTaxInPayment: {
        type: 'boolean',
        title: 'Collect County Tax in Payment',
        enum: [true, false],
        default: false,
      },
    },
  },
};

const marketScanMissouriTaxConfig = {
  MO: {
    // Missouri
    type: 'object',
    title: 'Missouri',
    properties: {
      noSalesTax: {
        type: 'boolean',
        title: 'Do Not Collect Sales Tax',
        enum: [true, false],
        default: false,
      },
    },
  },
};

const marketScanIllinoisTaxConfig = {
  IL: {
    // Illinois
    type: 'object',
    title: 'Illinois',
    properties: {
      noMonthlyUseTax: {
        type: 'boolean',
        title: 'Do Not Collect Monthly Use Tax',
        enum: [true, false],
        default: false,
      },
      taxCappedInterestOnSalesTax: {
        type: 'boolean',
        title: 'Collect Interest on Capped Sales Tax',
        enum: [true, false],
        default: false,
      },
    },
  },
};

const marketScanTaxConfigurationSchema = {
  taxConfigurations: {
    type: 'object',
    title: 'Tax Configurations',
    properties: {
      ...marketScanColoradoTaxConfig,
      ...marketScanMissouriTaxConfig,
      ...marketScanIllinoisTaxConfig,
    },
  },
};

const marketScanFeeConfigurationSchema = {
  feeConfigurations: {
    type: 'object',
    title: 'Fee Configurations',
    properties: {
      NH: {
        type: 'object',
        title: 'New Hampshire',
        properties: {
          includeOnlyTitleFeeForPurchase: {
            type: 'boolean',
            title: 'Include Only Title Fee for Purchase and Cash',
            enum: [true, false],
            default: false,
          },
          includeOnlyTitleFeeForLease: {
            type: 'boolean',
            title: 'Include Only Title Fee for Lease',
            enum: [true, false],
            default: false,
          },
        },
      },
    },
  },
};
